import type { ApiErrorBody, ErrorType } from "@fiskhub/shared";

/**
 * The dashboard's HTTP client.
 *
 * Dashboard routes are cookie-authenticated (§14): the session cookie is
 * httpOnly/Secure/SameSite=Lax, so it is never readable from JavaScript and is
 * attached by the browser via `credentials: "include"`.
 *
 * There is no CSRF token. It was removed at the owner's request, so what stops
 * a cross-site page from making a state-changing request on a live session is
 * `SameSite=Lax` — which withholds the cookie from any cross-site POST — plus
 * the API's CORS allowlist, which refuses the preflight a JSON request needs.
 * Nothing here has to read a cookie or set a header any more.
 */

/**
 * Where the API lives.
 *
 * `VITE_API_BASE_URL` is the API ORIGIN (`https://api.fiskhub.com`) — the same
 * value the Dockerfile bakes in and the deploy workflow passes per environment
 * — and the `/v1` prefix is appended here. Vite substitutes this at BUILD time;
 * there is no runtime config in a static bundle.
 *
 * Unset means development, where the fallback is the relative `/v1` that
 * vite.config.ts proxies to the local API. That fallback is ONLY correct behind
 * the dev proxy: in a deployed image it points at the nginx serving this very
 * bundle, whose SPA fallback answers a POST with 405 rather than anything that
 * looks like an API error. Keep the name in sync with the Dockerfile ARG.
 */
const API_ORIGIN = import.meta.env["VITE_API_BASE_URL"] as string | undefined;
const BASE = API_ORIGIN ? `${API_ORIGIN.replace(/\/+$/, "")}/v1` : "/v1";

/**
 * The `/v1` base every request in this app is built on.
 *
 * Exported for the playground, which shows it as a fixed, non-editable prefix.
 * That is deliberate: the playground takes a PATH, never a URL, so there is no
 * way to point it at another host and turn a signed-in dashboard into a request
 * forwarder.
 */
export const API_BASE = BASE;

/**
 * A failed API call, carrying the taxonomy from `packages/shared/errors.ts`
 * rather than a bare string. Screens branch on `code`, never on message text.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly type: ErrorType | "network_error";
  readonly code: string;
  readonly param: string | null;
  readonly upstream: { code: string; message: string } | null;
  readonly docUrl: string | null;
  /**
   * What actually failed, when the API is running outside production.
   *
   * A 500 otherwise reaches the screen as "an unexpected error occurred, quote
   * the request id", which is the right answer for a customer and a useless one
   * for whoever is looking at the dev server two windows away. The API only
   * sends this when `NODE_ENV !== "production"`, so it is `null` everywhere it
   * would matter.
   */
  readonly debug: string | null;

  constructor(
    status: number,
    body: ApiErrorBody["error"] | null,
    fallbackMessage: string,
    type: ErrorType | "network_error" = "internal_error",
  ) {
    const debug = typeof (body as { debug?: unknown } | null)?.debug === "string"
      ? ((body as unknown as { debug: string }).debug)
      : null;
    // Folded into the message rather than left as a separate field to render:
    // every error toast in the dashboard already shows `error.message`, and the
    // detail is worth nothing in a property nobody displays.
    super(debug ? `${body?.message ?? fallbackMessage} — ${debug}` : (body?.message ?? fallbackMessage));
    this.debug = debug;
    this.name = "ApiRequestError";
    this.status = status;
    this.type = body?.type ?? type;
    this.code = body?.code ?? "unknown_error";
    this.param = body?.param ?? null;
    this.upstream = body?.upstream ?? null;
    this.docUrl = body?.doc_url ?? null;
  }

  /** True when re-issuing the same request could plausibly succeed. */
  get retryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

export interface RequestOptions {
  method?: string;
  /** JSON body. Mutually exclusive with `form`. */
  body?: unknown;
  /** Multipart body — used for the PKCS#12 upload, which is never JSON. */
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Skip JSON parsing and hand back the raw response (CSV export). */
  raw?: boolean;
}

function buildUrl(path: string, query: RequestOptions["query"]): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/** Fired on 401 so the router can bounce to /login without a circular import. */
export const UNAUTHENTICATED_EVENT = "fiskhub:unauthenticated";

async function parseError(response: Response): Promise<ApiRequestError> {
  let body: ApiErrorBody["error"] | null = null;
  try {
    const parsed: unknown = await response.json();
    if (
      parsed &&
      typeof parsed === "object" &&
      "error" in parsed &&
      parsed.error &&
      typeof parsed.error === "object"
    ) {
      body = parsed.error as ApiErrorBody["error"];
    }
  } catch {
    // A non-JSON error body (a proxy 502 page, say) is still a real failure.
  }
  return new ApiRequestError(response.status, body, `Request failed with status ${response.status}`);
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers({ Accept: "application/json" });

  let payload: BodyInit | undefined;
  if (options.form) {
    // Do NOT set Content-Type — the browser must add the multipart boundary.
    payload = options.form;
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    payload = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: payload,
      // Sends the httpOnly session cookie. Without this the dashboard is anonymous.
      credentials: "include",
      signal: options.signal ?? null,
    });
  } catch (cause) {
    throw new ApiRequestError(
      0,
      null,
      cause instanceof Error && cause.name === "AbortError"
        ? "Request cancelled."
        : "Could not reach the FiskHub API. Check your connection and try again.",
      "network_error",
    );
  }

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    throw await parseError(response);
  }

  if (!response.ok) throw await parseError(response);

  if (options.raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  del: <T>(path: string, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "DELETE" }),
  upload: <T>(path: string, form: FormData, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "POST", form }),
};

/**
 * The list envelope every `GET` collection returns
 * (`openapi/fiskhub.v1.yaml`). Never a bare array.
 */
export interface ListPage<T> {
  object: "list";
  data: T[];
  has_more: boolean;
  next_cursor: string | null;
}

/**
 * Follow `next_cursor` until the API says there is no more, and return one
 * array.
 *
 * For the screens that show a whole collection at once — companies,
 * devices, certificates, users. Taking only the first page would quietly show a
 * subset: a tenant with 60 devices would see 50 of them, with nothing on screen
 * to say so, and "the device is not in the list" would look like a data bug.
 *
 * `MAX_PAGES` is a guard against a server that keeps handing back a cursor, not
 * a display limit. Hitting it throws rather than returning a short list, because
 * a truncated list that looks complete is the failure this function exists to
 * prevent.
 */
const MAX_PAGES = 40;

export async function fetchAll<T>(
  path: string,
  query: Record<string, unknown> = {},
): Promise<T[]> {
  const rows: T[] = [];
  let cursor: string | null = null;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const result: ListPage<T> = await api.get<ListPage<T>>(path, {
      query: { ...query, limit: 200, ...(cursor === null ? {} : { cursor }) },
    });
    rows.push(...result.data);
    if (!result.has_more || result.next_cursor === null) return rows;
    cursor = result.next_cursor;
  }

  throw new ApiRequestError(
    0,
    null,
    `${path} did not stop paginating after ${MAX_PAGES} pages (${rows.length} rows).`,
  );
}

/** Human-readable message for anything thrown out of a query or mutation. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

export function errorCode(error: unknown): string | null {
  return error instanceof ApiRequestError ? error.code : null;
}
